import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useMemo } from "react";

import type { SimpleProjectDto, UserDetailsDto, UserListItemDto, UserLookupDto } from "@/api/types";
import { Badge } from "@/design-system/Badge";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { ContextMenu, type ContextMenuItem } from "../ContextMenu/ContextMenu";
import { FormattedDate } from "../FormattedDate/FormattedDate";
import { User } from "../User/User";

type UserTableSortBy = ApiQueryParams<"getUsersV1">["SortBy"];

type TableSortBy = {
  key: UserTableSortBy | undefined;
  isDescending: boolean | undefined;
};

type UserTableRow = (UserListItemDto | UserLookupDto) & {
  project?: SimpleProjectDto;
};

export enum UserTableSensitiveColumn {
  CONTACT_INFO,
  PROJECT,
  ROLE,
  PLATFORM_DETAILS,
}

interface UserTableProps {
  data: UserTableRow[];
  isLoading?: boolean;
  sortBy?: TableSortBy;
  sensitiveColumns?: UserTableSensitiveColumn[];
  onSort?: ({ key, isDescending }: TableSortBy) => void;
  renderActions?: (user: UserTableRow) => ContextMenuItem[];
}

export function UserTable({
  data,
  isLoading,
  sortBy,
  sensitiveColumns = [],
  onSort,
  renderActions,
}: UserTableProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  const { data: userProjects = [] } = useLinkedProjects();

  const permissionQueries = usePermissionQueries();
  const {
    data: roles = [],
    isFetching: isFetchingRoles,
    isError: isErrorRoles,
  } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: sensitiveColumns.includes(UserTableSensitiveColumn.ROLE),
  });

  // Add new mapping for each column that should be sortable
  const sortMapTableToApi = {
    invitedAt: "invitedAt",
    registeredAt: "registeredAt",
    locatedAt: sessionUser.project.type === "addressBased" ? "address" : "company",
  } satisfies Partial<Record<keyof UserDetailsDto, UserTableSortBy>>;
  const sortMapApiToTable = {
    invitedAt: "invitedAt",
    registeredAt: "registeredAt",
    address: "locatedAt",
    company: "locatedAt",
  } satisfies Record<keyof UserTableSortBy, keyof UserDetailsDto>;
  const tableSorting =
    sortBy?.key && sortBy.key in sortMapApiToTable
      ? [
          {
            id: sortMapApiToTable[sortBy.key as keyof typeof sortMapApiToTable],
            desc: sortBy.isDescending ?? false,
          },
        ]
      : [];

  const columns = useMemo(() => {
    const sortableIds = Object.keys(sortMapTableToApi);
    const helper = createDashboardColumnHelper<UserTableRow>();

    return [
      helper.accessor("fullName", {
        header: t("component.user-table.headers.resident"),
        cell: ({ row }) => <User size="sm" user={row.original} projectSlugOverride={row.original.project?.slug} />,
        enableSorting: sortableIds.includes("fullName"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("email", {
        header: t("component.user-table.headers.email"),
        cell: (cell) => <span>{cell.getValue()}</span>,
        enableSorting: sortableIds.includes("email"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("project", {
        header: t("component.user-table.headers.project"),
        cell: (cell) => {
          const value = cell.getValue();

          if (!value) {
            return null;
          }

          return value.name;
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("locatedAt", {
        header:
          sessionUser.project.type === "addressBased"
            ? t("component.user-table.headers.address")
            : t("component.user-table.headers.company"),
        cell: (cell) => cell.getValue(),
        enableSorting: sortableIds.includes("locatedAt"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("deletedAt", {
        header: t("component.user-table.data.status"),
        cell: (cell) => {
          if (cell.getValue()) {
            return (
              <Badge data-testid="deleted-user-badge" variant="danger">
                {t("common.label.user-status.deleted")}
              </Badge>
            );
          } else if (cell.row.original.registeredAt) {
            return (
              <Badge data-testid="onboarded-user-badge" variant="success">
                {t("common.label.user-status.onboarded")}
              </Badge>
            );
          } else {
            return (
              <Badge data-testid="invited-user-badge" variant="warning">
                {t("common.label.user-status.invited")}
              </Badge>
            );
          }
        },
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
        enableSorting: false,
      }),
      helper.accessor("invitedAt", {
        header: t("component.user-table.headers.invitation-date"),
        cell: (cell) => {
          const value = cell.getValue();

          if (!value) {
            return null;
          }

          return <FormattedDate date={value} format="dateMonthYearShort" />;
        },
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        enableSorting: sortableIds.includes("invitedAt"),
        meta: {
          renderTruncatedTooltip: (cell) => {
            const value = cell.getValue();

            if (!value) {
              return null;
            }

            return <FormattedDate date={value} format="dateMonthYearShort" />;
          },
        },
      }),
      helper.accessor("registeredAt", {
        header: t("component.user-table.headers.activation-date"),
        cell: (cell) => {
          const value = cell.getValue();

          if (!value) {
            return null;
          }

          return <FormattedDate date={value} format="dateMonthYearShort" />;
        },
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        enableSorting: sortableIds.includes("registeredAt"),
        meta: {
          renderTruncatedTooltip: (cell) => {
            const value = cell.getValue();

            if (!value) {
              return null;
            }

            return <FormattedDate date={value} format="dateMonthYearShort" />;
          },
        },
      }),
      helper.accessor("roleId", {
        header: t("component.user-table.headers.role"),
        cell: (cell) => {
          const roleId = cell.getValue();
          const role = roles.find((r) => r.id === roleId);

          return <span className="whitespace-nowrap">{role?.name}</span>;
        },
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
        enableSorting: false,
      }),
      ...(renderActions
        ? [
            helper.display({
              id: "actions",
              cell: (cell) => {
                return <ContextMenu items={renderActions(cell.row.original)} />;
              },
              size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
              enableResizing: false,
              meta: {
                isAction: true,
              },
            }),
          ]
        : []),
    ];
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the React Compiler memoizes sortMapTableToApi
  }, [renderActions, roles, sessionUser.project.type, sortMapTableToApi, t]);

  const tableInstance = useResizableTable(TABLE_KEYS.USERS, {
    columns,
    data,
    state: {
      sorting: tableSorting,
      columnVisibility: {
        email: sensitiveColumns.includes(UserTableSensitiveColumn.CONTACT_INFO),
        project: sensitiveColumns.includes(UserTableSensitiveColumn.PROJECT),
        invitedAt: sensitiveColumns.includes(UserTableSensitiveColumn.PLATFORM_DETAILS),
        registeredAt: sensitiveColumns.includes(UserTableSensitiveColumn.PLATFORM_DETAILS),
        deletedAt: sensitiveColumns.includes(UserTableSensitiveColumn.PLATFORM_DETAILS),
        roleId: sensitiveColumns.includes(UserTableSensitiveColumn.ROLE) && !isErrorRoles,
        id: !!renderActions,
      } satisfies Partial<Record<keyof UserTableRow, boolean>>,
    },
    onSortingChange: onSort
      ? (updater) => {
          const newValue = typeof updater === "function" ? updater(tableSorting) : updater;
          const newSortBy = newValue?.[0];

          if (newSortBy && newSortBy.id in sortMapTableToApi) {
            const newSortKey = sortMapTableToApi[newSortBy.id as keyof typeof sortMapTableToApi];

            onSort({
              key: newSortKey,
              isDescending: newSortBy.desc,
            });

            return;
          }

          onSort({
            key: undefined,
            isDescending: false,
          });
        }
      : undefined,
    manualSorting: Boolean(sortBy),
    enableSortingRemoval: false,
  });

  return (
    <Table
      table={tableInstance}
      data-testid="users-list"
      getRowHref={(row) => {
        const targetProject = userProjects.find((p) => p.id === row.original.projectId);
        if (!targetProject) {
          return "";
        }

        return routes.users.details({
          slug: targetProject.slug,
          id: row.original.id,
        });
      }}
      isLoading={isLoading || isFetchingRoles}
    />
  );
}
